export const SERVICE_CATEGORIES = ['baumpflege', 'baumhausbau', 'baumfaellung'] as const;
export type ServiceCategory = (typeof SERVICE_CATEGORIES)[number];

export interface ProcessStep {
  title: string;
  description: string;
  icon: string;
}

export interface ServiceFAQ {
  question: string;
  answer: string;
}

export interface ServiceContent {
  slug: string;
  category: ServiceCategory;
  title: string;
  shortDescription: string;
  heroImage: string;
  description: string;
  whenNeeded: string[];
  process: ProcessStep[];
  faq: ServiceFAQ[];
  related: string[];
  metaDescription: string;
}

export const categoryMeta: Record<
  ServiceCategory,
  { title: string; intro: string; heroImage: string }
> = {
  baumpflege: {
    title: 'Baumpflege',
    intro:
      'Pflegende Eingriffe, die Vitalität, Standsicherheit und Verkehrssicherheit Ihrer Bäume erhalten – fachgerecht nach FLL-Richtlinien.',
    heroImage: '/images/placeholder-baumpflege.jpg',
  },
  baumhausbau: {
    title: 'Baumhausbau & Baubegleitung',
    intro:
      'Individuelle Baumhäuser, baumschonend befestigt und sicherheitstechnisch durchdacht – vom ersten Entwurf bis zur fertigen Plattform.',
    heroImage: '/images/placeholder-baumhausbau.jpg',
  },
  baumfaellung: {
    title: 'Baumfällung & Wurzelentfernung',
    intro:
      'Wenn Bäume zur Gefahr werden oder weichen müssen: kontrollierte Fällungen, auch in engsten Verhältnissen, mit sauberer Nacharbeit.',
    heroImage: '/images/placeholder-baumfaellung.jpg',
  },
};

const placeholder = (filename: string): string => `/images/placeholder-${filename}.jpg`;

const services: ServiceContent[] = [
  {
    slug: 'kronenpflege',
    category: 'baumpflege',
    title: 'Kronenpflege',
    shortDescription:
      'Fachgerechte Pflege der Baumkrone für Vitalität, Stabilität und Verkehrssicherheit.',
    heroImage: placeholder('kronenpflege'),
    description:
      'Kronenpflege ist die Pflege Ihres Baumes nach den Regeln der Baumbiologie und der ZTV-Baumpflege. Wir entnehmen Totholz, kappen keine gesunden Leitäste und respektieren den natürlichen Habitus des Baumes. Ziel ist immer der Erhalt einer vitalen, standsicheren Krone – nicht ihre Reduzierung.',
    whenNeeded: [
      'Totholz oder absterbende Äste in der Krone',
      'Reibäste oder Zwiesel mit eingewachsener Rinde',
      'Sichtkontrolle nach Stürmen oder Frostperioden',
      'Verkehrssicherungspflicht entlang Straßen, Wegen oder Spielflächen',
      'Vorbereitung auf Bauarbeiten in Wurzel- oder Kronenbereich',
    ],
    process: [
      {
        title: 'Sichtkontrolle vom Boden',
        description:
          'Wir beurteilen Habitus, Defektsymptome und Standortbedingungen vor dem Aufstieg.',
        icon: 'ScanEye',
      },
      {
        title: 'Aufstieg per Seilklettertechnik',
        description: 'Schonend für Stamm und Boden – ohne Hubarbeitsbühne, ohne Bodenschäden.',
        icon: 'Mountain',
      },
      {
        title: 'Selektiver Schnitt',
        description:
          'Entfernung von Totholz, Reibästen und Zwieseln nach ZTV-Baumpflege. Keine Kappungen.',
        icon: 'Scissors',
      },
      {
        title: 'Saubere Übergabe',
        description: 'Schnittgut entsorgt oder gehäckselt, Standort sauber hinterlassen.',
        icon: 'CircleCheck',
      },
    ],
    faq: [
      {
        question: 'Wie oft braucht ein Baum eine Kronenpflege?',
        answer:
          'Bei Großbäumen empfehlen wir alle 3 bis 5 Jahre eine Sichtkontrolle, je nach Standort und Baumart. Junge Bäume profitieren von einem Erziehungsschnitt im Abstand von 2 bis 3 Jahren.',
      },
      {
        question: 'Wird der Baum kleiner durch eine Kronenpflege?',
        answer:
          'Eine reine Pflege verändert die Größe nicht wesentlich. Wenn der Baum kleiner werden soll, ist eine Kroneneinkürzung das richtige Verfahren – und auch die nur in begrenztem Umfang.',
      },
      {
        question: 'Darf ich selbst in den Baum schneiden?',
        answer:
          'Kleine Eingriffe vom Boden aus sind möglich. Sobald Sie eine Leiter oder Säge brauchen oder am Baum klettern müssten, sollten Sie Profis ranlassen – aus Eigenschutz und um den Baum nicht zu schädigen.',
      },
    ],
    related: ['totholzentfernung', 'kroneneinkuerzung', 'baumkontrolle'],
    metaDescription:
      'Fachgerechte Kronenpflege in Hamburg & Schleswig-Holstein. Schonend per Seilklettertechnik, ohne Kappungen, nach ZTV-Baumpflege. Kostenloses Angebot.',
  },
  {
    slug: 'totholzentfernung',
    category: 'baumpflege',
    title: 'Totholzentfernung',
    shortDescription:
      'Verkehrssicherheit Ihrer Bäume wieder herstellen – durch gezieltes Entfernen abgestorbener Äste.',
    heroImage: '/images/totholz.jpg',
    description:
      'Trockene Äste werden ab einem bestimmten Durchmesser zur Gefahr für Personen und Eigentum. Mit dem Entfernen von Totholz werden Sie Ihrer Verkehrssicherungspflicht gerecht – ohne den Baum unnötig zu beschneiden. Wir arbeiten ab innen nach außen, mit Astklettern und Sicherungstechnik.',
    whenNeeded: [
      'Sichtbares Totholz über Wegen, Stellplätzen oder Spielflächen',
      'Nach Sturm- oder Trockenperioden',
      'Bei Verkehrssicherungspflicht (Anlieger, Verkehrssicherung)',
      'Vor Veranstaltungen mit Personenverkehr unter Bäumen',
    ],
    process: [
      {
        title: 'Begutachtung',
        description: 'Wir bewerten Menge, Stärke und Lage des Totholzes vom Boden aus.',
        icon: 'ScanEye',
      },
      {
        title: 'Sicherer Aufstieg',
        description: 'Seilklettertechnik mit Doppelseilsicherung, ohne Beschädigung der Rinde.',
        icon: 'Mountain',
      },
      {
        title: 'Astweise Entnahme',
        description: 'Größere Stücke werden abgeseilt, kleinere zu Boden gefällt.',
        icon: 'Axe',
      },
      {
        title: 'Aufräumen',
        description:
          'Schnittgut wird gehäckselt oder abgefahren – Sie übernehmen einen sauberen Garten.',
        icon: 'CircleCheck',
      },
    ],
    faq: [
      {
        question: 'Ab welcher Aststärke ist Totholz gefährlich?',
        answer:
          'Faustregel: Ab Daumenstärke (≥ 3 cm) und einer Fallhöhe über 4 m. In stark frequentierten Bereichen früher entfernen.',
      },
      {
        question: 'Stört Totholz den Baum?',
        answer:
          'Stehend lassen ist ökologisch sinnvoll (Habitat für Insekten und Vögel) – aber nur, wo niemand zu Schaden kommen kann. Über Wegen muss es entfernt werden.',
      },
    ],
    related: ['kronenpflege', 'baumkontrolle', 'sturmschaeden'],
    metaDescription:
      'Totholzentfernung professionell und schonend. Wir stellen die Verkehrssicherheit Ihrer Bäume wieder her – Hamburg, Stormarn, Lübeck.',
  },
  {
    slug: 'rueckschnitt',
    category: 'baumpflege',
    title: 'Rückschnitt',
    shortDescription:
      'Gezielter Rückschnitt zur Lichtgewinnung, für Gebäudefreischnitt oder zur Konfliktlösung mit Nachbarbebauung.',
    heroImage: placeholder('rueckschnitt'),
    description:
      'Ein Rückschnitt ist immer eine Abwägung zwischen dem Bedürfnis des Standorts und der Belastbarkeit des Baumes. Wir schneiden so viel wie nötig und so wenig wie möglich – jeden Schnitt am Astring, ohne Stummel, ohne Kappung.',
    whenNeeded: [
      'Äste reichen ans Haus, in Leitungen oder über die Grundstücksgrenze',
      'Beschattung von Wohnräumen oder Solaranlagen',
      'Konflikte mit Bauvorhaben oder Bestandsbauten',
    ],
    process: [
      {
        title: 'Maß nehmen',
        description: 'Wir definieren mit Ihnen den Zielzustand.',
        icon: 'Ruler',
      },
      {
        title: 'Schonender Aufstieg',
        description: 'Seilklettertechnik – kein Bohlenbelag, keine Kranfahrt.',
        icon: 'Mountain',
      },
      {
        title: 'Schnitt am Astring',
        description: 'Saubere Schnitte, die der Baum überwallen kann.',
        icon: 'Scissors',
      },
      {
        title: 'Sauber hinterlassen',
        description: 'Häckseln vor Ort oder Abtransport, je nach Wunsch.',
        icon: 'CircleCheck',
      },
    ],
    faq: [
      {
        question: 'Wie viel darf zurückgeschnitten werden?',
        answer:
          'Maximal 20–30 % der grünen Krone in einem Eingriff – mehr schadet dem Baum dauerhaft. Bei größerem Bedarf staffeln wir Eingriffe über mehrere Jahre.',
      },
    ],
    related: ['kroneneinkuerzung', 'fassadenschnitt', 'lichtraumprofilschnitt'],
    metaDescription:
      'Baumrückschnitt fachgerecht und schonend. Wir schneiden präzise – ohne Kappungen, nach ZTV-Baumpflege. Hamburg & Schleswig-Holstein.',
  },
  {
    slug: 'fassadenschnitt',
    category: 'baumpflege',
    title: 'Fassadenfreischnitt',
    shortDescription:
      'Gebäude vor anliegenden Ästen und Algenbewuchs schützen – Fassade frei, Baum erhalten.',
    heroImage: placeholder('fassade'),
    description:
      'An Häusern, Garagen und Carports schaben Äste die Putz- und Holzschicht ab und halten Feuchtigkeit gegen die Fassade. Wir schneiden den Baum so weit zurück, dass die Fassade lüftet, ohne den Baum zu deformieren.',
    whenNeeded: [
      'Äste reiben an Putz, Klinker oder Holzfassade',
      'Algen- und Moosbildung durch ständig feuchte Wandbereiche',
      'Verschattung von Fenstern oder Solaranlagen',
    ],
    process: [
      { title: 'Vor Ort prüfen', description: 'Was kann weg, was muss bleiben.', icon: 'ScanEye' },
      {
        title: 'Sichern und schneiden',
        description: 'Mit Seilklettertechnik präzise an der Fassade arbeiten.',
        icon: 'Scissors',
      },
      {
        title: 'Aufräumen',
        description: 'Auch schmale Hofdurchgänge bleiben sauber.',
        icon: 'CircleCheck',
      },
    ],
    faq: [
      {
        question: 'Wie nah darf der Baum am Haus stehen?',
        answer:
          'Eine generelle Regel gibt es nicht. Wichtig ist ein Mindestabstand von ca. 1 m zwischen Krone und Fassade, damit Lüftung und Trocknung gewährleistet sind.',
      },
    ],
    related: ['rueckschnitt', 'lichtraumprofilschnitt', 'kroneneinkuerzung'],
    metaDescription:
      'Fassadenfreischnitt schonend und sauber. Wir halten Ihren Baum gesund und Ihre Fassade trocken.',
  },
  {
    slug: 'lichtraumprofilschnitt',
    category: 'baumpflege',
    title: 'Lichtraumprofilschnitt',
    shortDescription: 'Freier Verkehrsraum nach FLL: 4,50 m über Fahrbahn, 2,50 m über Gehweg.',
    heroImage: placeholder('lichtraum'),
    description:
      'Lichtraumprofilschnitt sichert den freien Verkehrsraum nach den FLL-Richtlinien. Wir kürzen überhängende Äste auf das vorgeschriebene Maß ein – schonend und ohne den Habitus zu zerstören.',
    whenNeeded: [
      'Überhängende Äste an Fahrbahn, Gehweg oder Radweg',
      'Beschilderung oder Beleuchtung wird verdeckt',
      'Behördliche Aufforderung an Anlieger',
    ],
    process: [
      {
        title: 'Maße prüfen',
        description: 'Wir messen das aktuelle Lichtraumprofil.',
        icon: 'Ruler',
      },
      {
        title: 'Schonender Schnitt',
        description: 'Astweise zurück bis ans Ableitungsast – keine Stummel.',
        icon: 'Scissors',
      },
      {
        title: 'Verkehrssicherung',
        description: 'Bei Bedarf mit Sperrung und TLS-Schein.',
        icon: 'TriangleAlert',
      },
    ],
    faq: [
      {
        question: 'Welche Maße gelten?',
        answer:
          'Über Fahrbahnen 4,50 m, über Gehwegen 2,50 m. Über Radwegen 2,50 m. Maßgeblich ist der höchste zulässige Fahrzeugaufbau plus Sicherheitsreserve.',
      },
    ],
    related: ['rueckschnitt', 'fassadenschnitt', 'baumkontrolle'],
    metaDescription:
      'Lichtraumprofilschnitt nach FLL. Wir sichern Ihren Verkehrsraum schnell und fachgerecht – Schleswig-Holstein und Hamburg.',
  },
  {
    slug: 'kroneneinkuerzung',
    category: 'baumpflege',
    title: 'Kroneneinkürzung',
    shortDescription:
      'Reduzierung der Kronengröße in begrenztem Umfang – als Alternative zur Fällung.',
    heroImage: placeholder('einkuerzung'),
    description:
      'Eine Kroneneinkürzung verringert das Hebelmoment auf den Stamm – wichtig bei Standsicherheitsdefiziten oder Konflikten mit Bebauung. Sie ist kein Ersatz für regelmäßige Pflege, aber oft die letzte Alternative zur Fällung.',
    whenNeeded: [
      'Standsicherheitsdefizit nach FLL-Baumkontrolle',
      'Ungünstige Statik (z. B. einseitige Krone)',
      'Konflikte mit Bebauung, die anders nicht lösbar sind',
    ],
    process: [
      {
        title: 'Statische Bewertung',
        description: 'Wir prüfen, ob eine Einkürzung den Baum erhalten kann.',
        icon: 'ShieldCheck',
      },
      {
        title: 'Schnittplan',
        description: 'Maximal 20–30 % Reduktion, jeder Schnitt am Ableitungsast.',
        icon: 'Pencil',
      },
      {
        title: 'Ausführung',
        description: 'Per Seilklettertechnik, mit Abseilen größerer Stücke.',
        icon: 'Mountain',
      },
      {
        title: 'Folgepflege',
        description: 'Empfehlung für Nachsorge in 3–5 Jahren.',
        icon: 'Calendar',
      },
    ],
    faq: [
      {
        question: 'Was ist der Unterschied zur Kappung?',
        answer:
          'Eine Kappung ist ein willkürlicher Schnitt mitten ins Astgefüge. Eine Einkürzung schneidet immer auf einen Ableitungsast – der Baum kann den Schnitt überwallen und weiter wachsen.',
      },
    ],
    related: ['rueckschnitt', 'kronensicherung', 'kronenpflege'],
    metaDescription:
      'Kroneneinkürzung schonend und fachgerecht – als Alternative zur Fällung. Hamburg & Schleswig-Holstein.',
  },
  {
    slug: 'jungbaumpflege',
    category: 'baumpflege',
    title: 'Jungbaumpflege & Erziehungsschnitt',
    shortDescription: 'Frühzeitige Pflege – damit Ihr Baum in 30 Jahren noch eine gute Krone hat.',
    heroImage: placeholder('jungbaum'),
    description:
      'In den ersten Standjahren entscheidet sich, ob ein Baum im Alter standsicher und harmonisch bleibt. Wir leiten die Krone an, fördern einen klaren Stamm und vermeiden gefährliche Zwiesel.',
    whenNeeded: [
      'Neupflanzungen 2–10 Jahre nach dem Setzen',
      'Aufbau eines klaren Stammes für die Entwicklung der Krone',
      'Korrektur von Zwieselbildung, Konkurrenztrieben',
    ],
    process: [
      {
        title: 'Aufbau begutachten',
        description: 'Wir definieren das Erziehungsziel.',
        icon: 'ScanEye',
      },
      {
        title: 'Leittrieb herausarbeiten',
        description: 'Konkurrenz wird gekürzt oder entnommen.',
        icon: 'Scissors',
      },
      {
        title: 'Folgeplanung',
        description: 'Empfehlung für die nächsten 2–3 Jahre.',
        icon: 'Calendar',
      },
    ],
    faq: [
      {
        question: 'Wie oft braucht ein Jungbaum Pflege?',
        answer:
          'In den ersten 10 Jahren alle 2–3 Jahre. Danach reicht meist ein Pflegeintervall von 5 Jahren.',
      },
    ],
    related: ['kronenpflege', 'baumkontrolle', 'kroneneinkuerzung'],
    metaDescription:
      'Jungbaumpflege und Erziehungsschnitt. Wir bauen die Krone Ihres jungen Baumes harmonisch und standsicher auf.',
  },
  {
    slug: 'kronensicherung',
    category: 'baumpflege',
    title: 'Kronensicherung',
    shortDescription:
      'Dynamische Sicherung von Zwieseln und Schwachstellen – Erhalt statt Fällung.',
    heroImage: placeholder('kronensicherung'),
    description:
      'Mit textilen Gurtsystemen (z. B. Cobra-Sicherung) verbinden wir kritische Kronenpartien dynamisch miteinander. Der Baum bleibt beweglich, Schwachstellen werden gegen Bruch gesichert. Eine Kronensicherung ist oft die schonendste Alternative zu einer Einkürzung oder Fällung.',
    whenNeeded: [
      'Zwiesel mit eingewachsener Rinde',
      'Risse an Stammverzweigungen',
      'Anwachsungen, die statisch fragwürdig sind',
    ],
    process: [
      {
        title: 'Statische Beurteilung',
        description: 'Welche Punkte müssen gesichert werden?',
        icon: 'ShieldCheck',
      },
      {
        title: 'System auswählen',
        description: 'Statisch / dynamisch, Tragkraft, Lebensdauer.',
        icon: 'Wrench',
      },
      {
        title: 'Einbau',
        description: 'Per Seilklettertechnik, ohne Verletzung der Rinde.',
        icon: 'Mountain',
      },
      {
        title: 'Wartung',
        description: 'Empfehlung zur Sichtkontrolle alle 2 Jahre.',
        icon: 'Calendar',
      },
    ],
    faq: [
      {
        question: 'Wie lange hält eine Kronensicherung?',
        answer:
          'Hochwertige Systeme sind auf 8–10 Jahre ausgelegt. Wir empfehlen Sichtkontrollen alle 2 Jahre und Austausch nach Hersteller-Vorgabe.',
      },
    ],
    related: ['kroneneinkuerzung', 'baumkontrolle', 'kronenpflege'],
    metaDescription:
      'Kronensicherung mit Cobra-Systemen. Wir erhalten Ihren Baum, statt ihn zu fällen – schonend und dauerhaft.',
  },
  {
    slug: 'baumkontrolle',
    category: 'baumpflege',
    title: 'Baumkontrolle nach FLL',
    shortDescription: 'Verkehrssicherheit dokumentiert – mit Bericht und Maßnahmenempfehlung.',
    heroImage: '/images/begutachtung.jpg',
    description:
      'Eigentümer haben eine Verkehrssicherungspflicht. Mit einer dokumentierten Regelkontrolle nach FLL sind Sie auf der sicheren Seite. Wir erstellen Bericht, Bewertung und Maßnahmenliste – als Bestandteil Ihres Baumkatasters oder als Einzelgutachten.',
    whenNeeded: [
      'Bestandsbäume auf Privat- oder Gewerbegrundstücken',
      'Vor Grundstücksverkauf oder Bauvorhaben',
      'Nach auffälligen Symptomen (Trockenheit, Pilze, Schiefstand)',
    ],
    process: [
      {
        title: 'Vorbegehung',
        description: 'Wir verschaffen uns Überblick und definieren Umfang.',
        icon: 'ScanEye',
      },
      {
        title: 'Visuelle Kontrolle',
        description: 'Vom Boden, ggf. ergänzt durch eingehende Untersuchung.',
        icon: 'Eye',
      },
      {
        title: 'Dokumentation',
        description: 'Bericht mit Bewertung, Fotos und Maßnahmenempfehlung.',
        icon: 'FileText',
      },
      {
        title: 'Beratung',
        description: 'Wir besprechen Prioritäten und Zeitachse mit Ihnen.',
        icon: 'MessagesSquare',
      },
    ],
    faq: [
      {
        question: 'Wie oft sollte ich kontrollieren lassen?',
        answer:
          'Standardintervall ist 1–2 mal pro Jahr (Vegetations- und Ruhezeit). Bei Risikobäumen häufiger, bei kontrollierten Beständen seltener.',
      },
    ],
    related: ['kronenpflege', 'totholzentfernung', 'kronensicherung'],
    metaDescription:
      'Baumkontrolle nach FLL mit Bericht. Wir dokumentieren die Verkehrssicherheit Ihrer Bäume – Hamburg & Schleswig-Holstein.',
  },
  {
    slug: 'sturmschaeden',
    category: 'baumpflege',
    title: 'Sturmschäden',
    shortDescription:
      'Schnelle Hilfe nach dem Sturm – am selben Tag, oft innerhalb weniger Stunden.',
    heroImage: placeholder('sturm'),
    description:
      'Abgebrochene Äste, gespaltene Stämme, umgestürzte Bäume auf Auto, Dach oder Stromleitung: Wir kommen schnell und sichern die Lage. Mit Seilklettertechnik und stückweisem Abtragen, ohne Folgeschäden.',
    whenNeeded: [
      'Akute Sturmschäden mit Personen- oder Sachgefährdung',
      'Hängengebliebene Äste in Kronen',
      'Bruch entlang von Wegen oder Stromleitungen',
    ],
    process: [
      {
        title: 'Anruf entgegennehmen',
        description: 'Wir nehmen sofort Kontakt auf, klären Zugang und Risiko.',
        icon: 'Phone',
      },
      {
        title: 'Anfahrt',
        description: 'In der Regel binnen Stunden im Einsatzgebiet vor Ort.',
        icon: 'Car',
      },
      {
        title: 'Sicherung',
        description: 'Akute Gefahren werden zuerst entschärft.',
        icon: 'ShieldCheck',
      },
      {
        title: 'Rückbau',
        description: 'Stückweises Abtragen, Abtransport, Versicherungsdoku.',
        icon: 'CircleCheck',
      },
    ],
    faq: [
      {
        question: 'Übernimmt die Versicherung die Kosten?',
        answer:
          'In der Regel ja – Wohngebäude- oder Elementarschadenversicherung. Wir dokumentieren den Schaden mit Fotos und Bericht für Sie.',
      },
    ],
    related: ['notfaellung', 'gefahrenfaellung', 'totholzentfernung'],
    metaDescription:
      'Sturmschäden 24/7. Wir kommen schnell, sichern die Lage und dokumentieren für die Versicherung. Hamburg & Schleswig-Holstein.',
  },
  {
    slug: 'individueller-baumhausbau',
    category: 'baumhausbau',
    title: 'Individueller Baumhausbau',
    shortDescription:
      'Vom Familienprojekt bis zur Glamping-Plattform – baumschonend und langlebig.',
    heroImage: placeholder('baumhaus-individuell'),
    description:
      'Wir bauen Baumhäuser, die Ihren Baum respektieren und Generationen halten. Konstruktion, Befestigungen und Materialien werden auf Ihre Wunschnutzung und den Trägerbaum abgestimmt – nicht umgekehrt.',
    whenNeeded: [
      'Familienprojekt für Kinder',
      'Glamping- oder Übernachtungsplattform',
      'Naturnaher Ruhe- oder Arbeitsplatz im Garten',
    ],
    process: [
      {
        title: 'Wunsch und Standort',
        description: 'Wir hören zu und schauen uns Ihren Baum an.',
        icon: 'MessagesSquare',
      },
      {
        title: 'Entwurf',
        description: 'Skizze + Materialliste + Festpreis-Angebot.',
        icon: 'Pencil',
      },
      { title: 'Bau', description: 'Vor Ort, mit baumschonenden Befestigungen.', icon: 'Hammer' },
      {
        title: 'Übergabe',
        description: 'Inkl. Pflegehinweisen für Baum und Konstruktion.',
        icon: 'CircleCheck',
      },
    ],
    faq: [
      {
        question: 'Welcher Baum eignet sich?',
        answer:
          'Eichen, Buchen, Robinien, Kastanien – also Hartholzbäume mit kräftigem Stamm. Wir prüfen den konkreten Baum vor Ort.',
      },
    ],
    related: ['planung-beratung', 'baumschonende-befestigung', 'familienprojekte'],
    metaDescription:
      'Individueller Baumhausbau in Hamburg & Schleswig-Holstein. Baumschonend befestigt, langlebig konstruiert, sicherheitsgeprüft.',
  },
  {
    slug: 'planung-beratung',
    category: 'baumhausbau',
    title: 'Planung & Beratung',
    shortDescription: 'Standortwahl, Statik, Baumeignung – wir denken vor dem ersten Holz.',
    heroImage: placeholder('planung'),
    description:
      'Bevor das erste Brett montiert wird, entscheidet die Planung über Lebensdauer und Sicherheit. Wir bewerten Trägerbaum, Statik, Nutzungsidee und Genehmigungspflicht – und liefern eine Entwurfsskizze als Grundlage für den Bau.',
    whenNeeded: [
      'Erste Idee soll konkret werden',
      'Eignung des Wunschbaumes prüfen',
      'Verbindliche Materialliste und Kostenschätzung',
    ],
    process: [
      { title: 'Vor-Ort-Termin', description: 'Wir nehmen Maß am Trägerbaum.', icon: 'Ruler' },
      { title: 'Skizze', description: 'Plattform, Aufgang, Geländer, Dach.', icon: 'Pencil' },
      {
        title: 'Materialliste',
        description: 'Holz, Beschläge, Sicherheitsausrüstung.',
        icon: 'List',
      },
      { title: 'Festpreis', description: 'Schriftliches Angebot, transparent.', icon: 'FileText' },
    ],
    faq: [
      {
        question: 'Brauche ich eine Baugenehmigung?',
        answer:
          'In Schleswig-Holstein meist nicht – aber das hängt von Größe, Bebauungsplan und Nutzung ab. Wir klären das vor dem Bau mit Ihnen.',
      },
    ],
    related: ['individueller-baumhausbau', 'sicherheitskonzepte', 'baubegleitung'],
    metaDescription:
      'Planung und Beratung für Ihren Baumhausbau. Standort, Statik und Genehmigung im Blick.',
  },
  {
    slug: 'baumschonende-befestigung',
    category: 'baumhausbau',
    title: 'Baumschonende Befestigung',
    shortDescription:
      'TAB- und Garnier-Limb-Systeme: Tragfähigkeit ohne Strangulieren des Stammes.',
    heroImage: placeholder('befestigung'),
    description:
      'Verspannungen mit Bändern oder Gurten würgen den Stamm ab und sind langfristig keine Lösung. Wir verwenden TAB-Bolzen oder Garnier-Limb-Systeme: hohe Tragkraft, kontrollierte Wundbildung, der Baum kann ungestört weiterwachsen.',
    whenNeeded: [
      'Plattformlasten ab 100 kg/Punkt',
      'Mehrjährige Konstruktionen',
      'Trägerbäume, die sich gesund weiterentwickeln sollen',
    ],
    process: [
      {
        title: 'System wählen',
        description: 'TAB, Garnier-Limb oder Spezialanker, je nach Last.',
        icon: 'Wrench',
      },
      {
        title: 'Setzen',
        description: 'Sauberes Bohren, korrektes Setzen, kontrollierte Verbindung.',
        icon: 'Hammer',
      },
      {
        title: 'Dokumentieren',
        description: 'Position und Belastbarkeit für die Statik.',
        icon: 'FileText',
      },
    ],
    faq: [
      {
        question: 'Was passiert mit dem Baum, wenn die TAB hineingeht?',
        answer:
          'Der Baum überwallt die Bohrung, kapselt die TAB ab. Bei richtiger Setztiefe und -ausführung kein Problem für die Vitalität.',
      },
    ],
    related: ['individueller-baumhausbau', 'sicherheitskonzepte', 'materialwahl'],
    metaDescription:
      'Baumschonende Befestigungen für Baumhäuser. TAB-Systeme und Garnier-Limb – fachgerecht eingebaut.',
  },
  {
    slug: 'sicherheitskonzepte',
    category: 'baumhausbau',
    title: 'Sicherheitskonzepte',
    shortDescription: 'Von Geländerhöhe bis Notabstieg – wir denken Sicherheit von Anfang an mit.',
    heroImage: placeholder('sicherheit'),
    description:
      'Ein Baumhaus ist erst gut, wenn es sicher ist. Wir konzipieren Geländer, Aufstieg, Lasten und Wartungsintervalle nach DIN-EN-Spielplatzgeräten und Industrienorm – auch für Familienprojekte.',
    whenNeeded: [
      'Plattformhöhe über 2 m',
      'Übernachtungsnutzung',
      'Gewerbliche Nutzung (Glamping, Workshops)',
    ],
    process: [
      {
        title: 'Risikoanalyse',
        description: 'Wer nutzt es wie, mit welchem Vorwissen?',
        icon: 'ShieldCheck',
      },
      {
        title: 'Konstruktion',
        description: 'Geländer, Aufstieg, Bodenbeläge nach Standard.',
        icon: 'Hammer',
      },
      {
        title: 'Abnahme',
        description: 'Sichtkontrolle und Lastprobe nach Fertigstellung.',
        icon: 'CircleCheck',
      },
    ],
    faq: [
      {
        question: 'Wie hoch muss das Geländer sein?',
        answer:
          'In Anlehnung an DIN EN 1176 mindestens 70 cm bei Plattformen ab 1 m, 100 cm ab 2 m Höhe. Wir gehen meist sicherheitshalber auf 110 cm.',
      },
    ],
    related: ['planung-beratung', 'baumschonende-befestigung', 'baubegleitung'],
    metaDescription:
      'Sicherheitskonzepte für Baumhäuser. Geländer, Aufstieg, Wartung – nach Norm und mit Augenmaß.',
  },
  {
    slug: 'materialwahl',
    category: 'baumhausbau',
    title: 'Materialwahl',
    shortDescription: 'Lärche, Eiche, Douglasie – die richtige Holzart für 30 Jahre Lebensdauer.',
    heroImage: placeholder('material'),
    description:
      'Im Außenbereich entscheidet die Holzart über die Lebensdauer. Wir setzen auf Lärche, Eiche oder Douglasie (heimisch, langlebig) und Edelstahl-Beschläge. Kein Tropenholz, keine Druckimprägnierung in Greifhöhe.',
    whenNeeded: ['Plattform und Tragwerk', 'Geländer und Aufstieg', 'Beschläge und Verbinder'],
    process: [
      {
        title: 'Anforderungen klären',
        description: 'Lasten, Optik, Pflege, Budget.',
        icon: 'List',
      },
      {
        title: 'Holzart vorschlagen',
        description: 'Mit Argumenten – nicht nach Mode.',
        icon: 'Trees',
      },
      { title: 'Beschläge', description: 'Edelstahl A2/A4 statt verzinkt.', icon: 'Wrench' },
    ],
    faq: [
      {
        question: 'Hält Lärche wirklich 30 Jahre?',
        answer:
          'Bei sachgerechter Konstruktion (Wasser läuft ab, Holz trocknet, kein Erdkontakt) – ja. Pflegeintervalle alle 3–5 Jahre verlängern die Lebensdauer.',
      },
    ],
    related: ['planung-beratung', 'baumschonende-befestigung', 'individueller-baumhausbau'],
    metaDescription:
      'Materialwahl für Baumhäuser: heimische Hölzer, Edelstahl-Beschläge, langlebig und ehrlich.',
  },
  {
    slug: 'baubegleitung',
    category: 'baumhausbau',
    title: 'Baubegleitung',
    shortDescription:
      'Sie bauen selbst – wir begleiten an den kritischen Stellen (Statik, Befestigung, Abnahme).',
    heroImage: placeholder('baubegleitung'),
    description:
      'Sie haben Spaß am Bauen, wollen aber an den kritischen Punkten Profis dabei haben? Wir kommen für Statik, Befestigung und Abnahme – Sie arbeiten dazwischen selbst.',
    whenNeeded: [
      'Eigenleistung bei Familien- oder Vereinsprojekten',
      'Mischmodell aus DIY und Fachfirma',
      'Sichere Abnahme nach Fertigstellung',
    ],
    process: [
      {
        title: 'Auftakt-Termin',
        description: 'Wir besprechen, wo wir gebraucht werden.',
        icon: 'MessagesSquare',
      },
      {
        title: 'Pflicht-Stationen',
        description: 'Befestigung, Tragwerk, Geländer, Abnahme.',
        icon: 'CircleCheck',
      },
      {
        title: 'Auf Abruf',
        description: 'Sie melden sich, wenn Sie an einen kritischen Punkt kommen.',
        icon: 'Phone',
      },
    ],
    faq: [
      {
        question: 'Wie wird abgerechnet?',
        answer:
          'Wir vereinbaren einen Tagessatz für Vor-Ort-Termine. Beratung am Telefon ist im Paket inklusive.',
      },
    ],
    related: ['planung-beratung', 'sicherheitskonzepte', 'baumschonende-befestigung'],
    metaDescription:
      'Baubegleitung für Selbstbauer: Wir kommen für Statik, Befestigung und Abnahme – Sie bauen dazwischen selbst.',
  },
  {
    slug: 'familienprojekte',
    category: 'baumhausbau',
    title: 'Familienprojekte',
    shortDescription:
      'Baumhäuser, die Generationen halten – sicher genug für die Kinder, schön genug für die Eltern.',
    heroImage: placeholder('familie'),
    description:
      'Familienbaumhäuser sind unsere häufigsten Aufträge. Wir setzen auf eine klare Plattform, sicheres Geländer, einen einfachen Aufstieg – und lassen Raum für die kindliche Fantasie. Kein Disney, kein Spielzeug-Look.',
    whenNeeded: [
      'Kinder zwischen 4 und 14 Jahren',
      'Stabiler Trägerbaum im Garten',
      'Zwei bis vier Wochen Bauzeit',
    ],
    process: [
      {
        title: 'Mit der Familie sprechen',
        description: 'Wer nutzt es wofür, wie groß?',
        icon: 'MessagesSquare',
      },
      {
        title: 'Bauen',
        description: 'Plattform, Geländer, Aufstieg – mit den Kindern, wenn gewünscht.',
        icon: 'Hammer',
      },
      {
        title: 'Übergabe',
        description: 'Wir zeigen Sicherheits-Hotspots und Pflege.',
        icon: 'CircleCheck',
      },
    ],
    faq: [
      {
        question: 'Können wir mit den Kindern mitbauen?',
        answer:
          'An ungefährlichen Stationen ja, das macht Spaß und schafft Bindung. Wir koordinieren das mit Ihnen.',
      },
    ],
    related: ['individueller-baumhausbau', 'sicherheitskonzepte', 'baubegleitung'],
    metaDescription:
      'Familienbaumhäuser bauen lassen – sicher, schön, langlebig. Hamburg & Schleswig-Holstein.',
  },
  {
    slug: 'baumfaellung',
    category: 'baumfaellung',
    title: 'Baumfällung',
    shortDescription: 'Geplante Fällung mit klarem Vorlauf – sauber, schnell, sicher.',
    heroImage: placeholder('faellung'),
    description:
      'Eine geplante Fällung beginnt mit einer ehrlichen Beratung – manchmal ist Pflege die bessere Option. Wenn die Fällung nötig ist, planen wir Zugang, Fallrichtung und Entsorgung gemeinsam. Mit oder ohne Seilklettertechnik, je nach Standort.',
    whenNeeded: [
      'Standsicherheit nicht mehr gewährleistet',
      'Bauliche Veränderungen am Standort',
      'Genehmigte Fällung nach Baumschutzsatzung',
    ],
    process: [
      {
        title: 'Genehmigung prüfen',
        description: 'Wir klären, ob ein Antrag nötig ist.',
        icon: 'FileText',
      },
      {
        title: 'Fällrichtung planen',
        description: 'Standort, Hindernisse, Wind, Stamm.',
        icon: 'Compass',
      },
      {
        title: 'Fällung ausführen',
        description: 'Konventionell oder stückweise per Seilklettertechnik.',
        icon: 'Axe',
      },
      { title: 'Entsorgung', description: 'Holz, Schnittgut, Stubben.', icon: 'Truck' },
    ],
    faq: [
      {
        question: 'Wann ist eine Fällung erlaubt?',
        answer:
          'Außerhalb der Vogelschutzfrist (1.10.–28.2.) ist die Fällung in den meisten Gemeinden möglich. Bei Baumschutzsatzung ist immer eine Genehmigung nötig.',
      },
    ],
    related: ['gefahrenfaellung', 'seilklettertechnik', 'stueckweises-abtragen'],
    metaDescription:
      'Baumfällung professionell und sauber. Wir fällen schonend für Garten und Umfeld – Hamburg & Schleswig-Holstein.',
  },
  {
    slug: 'gefahrenfaellung',
    category: 'baumfaellung',
    title: 'Gefahrenfällung',
    shortDescription: 'Wenn der Baum statisch fragwürdig ist – Sicherung, Rückbau, kein Risiko.',
    heroImage: placeholder('gefahrenfaellung'),
    description:
      'Bei statisch unsicheren Bäumen verzichten wir auf konventionelle Fällung. Stattdessen wird der Baum von oben stückweise abgetragen, ggf. mit Hubsteiger oder Kran gesichert.',
    whenNeeded: [
      'Stammrisse, Pilzbefall, Wurzelschäden',
      'Schiefstellung mit Bewegung',
      'Bäume nahe Bebauung oder Stromleitungen',
    ],
    process: [
      {
        title: 'Statik-Bewertung',
        description: 'Wo sind die Schwachstellen, wie wirken Lasten?',
        icon: 'ShieldCheck',
      },
      {
        title: 'Sicherungstechnik',
        description: 'Seile, ggf. Kran oder Hubsteiger.',
        icon: 'Wrench',
      },
      {
        title: 'Stückweises Abtragen',
        description: 'Kein freier Fall, jeder Abschnitt kontrolliert.',
        icon: 'Axe',
      },
    ],
    faq: [
      {
        question: 'Können Sie auch über Häusern arbeiten?',
        answer:
          'Ja, mit Abseiltechnik und ggf. Kraneinsatz. Wir koordinieren ggf. Straßensperrung mit der Behörde.',
      },
    ],
    related: ['baumfaellung', 'seilklettertechnik', 'sturmschaeden'],
    metaDescription:
      'Gefahrenfällung schonend und sicher – auch über Bebauung und in engen Innenhöfen.',
  },
  {
    slug: 'seilklettertechnik',
    category: 'baumfaellung',
    title: 'Seilklettertechnik (SKT)',
    shortDescription: 'Aufstieg ohne Hubarbeitsbühne – schonend für Boden, Stamm und Geldbeutel.',
    heroImage: '/images/seilklettern.jpg',
    description:
      'Seilklettertechnik ist unsere Standard-Aufstiegsmethode. Doppelseilsicherung, geprüfte PSAgA, regelmäßige Schulungen – wir kommen überall hin, wo kein Hubsteiger steht oder steht.',
    whenNeeded: [
      'Enge Innenhöfe, Hinterhof, Hanglage',
      'Empfindlicher Boden (Wiese, Pflaster, Beete)',
      'Bäume mit eingewachsenem Habitus',
    ],
    process: [
      {
        title: 'Stammkontrolle',
        description: 'Vor Aufstieg auf Risse, Pilze, Hohlräume prüfen.',
        icon: 'Eye',
      },
      {
        title: 'Aufstieg',
        description: 'Doppelseil, PSAgA, durchgehend gesichert.',
        icon: 'Mountain',
      },
      {
        title: 'Arbeiten in der Krone',
        description: 'Mit Positionierungs- und Arbeitsseilen.',
        icon: 'Wrench',
      },
    ],
    faq: [
      {
        question: 'Ist Seilklettern teurer als Hubsteiger?',
        answer:
          'Oft günstiger – kein Bewegen schwerer Maschinen, kein Pflasterschaden. In Innenstadtlagen ist SKT meist die einzige praktikable Methode.',
      },
    ],
    related: ['baumfaellung', 'kronenpflege', 'stueckweises-abtragen'],
    metaDescription:
      'Seilklettertechnik (SKT) für Baumarbeiten. Schonend, präzise, auch im engen Innenhof.',
  },
  {
    slug: 'stueckweises-abtragen',
    category: 'baumfaellung',
    title: 'Stückweises Abtragen',
    shortDescription: 'Wenn der Platz nicht reicht: Krone und Stamm abschnittweise abseilen.',
    heroImage: placeholder('abtragen'),
    description:
      'Stückweises Abtragen ist die Methode für Bäume, die nicht gefällt werden können – sei es wegen Bebauung, Pflaster oder Vegetation drumherum. Jedes Stück wird abgeseilt, kein freier Fall, kein Schaden.',
    whenNeeded: [
      'Bäume direkt neben Häusern',
      'Pflaster oder empfindliche Beete unter dem Baum',
      'Stromleitungen oder Glasdächer im Fallbereich',
    ],
    process: [
      {
        title: 'Plan',
        description: 'Welche Astpartien zuerst, welche Hebepunkte?',
        icon: 'Pencil',
      },
      {
        title: 'Abseilen',
        description: 'Mit Friktionsbremse oder Lowering-Device.',
        icon: 'Wrench',
      },
      { title: 'Abtransport', description: 'Vor Ort oder per Container.', icon: 'Truck' },
    ],
    faq: [
      {
        question: 'Wie lange dauert das Abtragen?',
        answer:
          'Ein Großbaum kann 1–2 Tage dauern, je nach Größe und Zugang. Wir koordinieren mit Ihnen, ob durchgehend oder gestaffelt.',
      },
    ],
    related: ['gefahrenfaellung', 'seilklettertechnik', 'baumfaellung'],
    metaDescription:
      'Stückweises Abtragen großer Bäume in engen Lagen – sauber, sicher, ohne Schäden am Umfeld.',
  },
  {
    slug: 'entsorgung',
    category: 'baumfaellung',
    title: 'Entsorgung',
    shortDescription:
      'Häckseln, Brennholz, Containerdienst – wir nehmen alles mit oder lassen, was Sie wollen.',
    heroImage: placeholder('entsorgung'),
    description:
      'Schnittgut können wir vor Ort häckseln (als Mulch für Sie oder zum Abtransport), Stämme zu Brennholz aufarbeiten oder per Container entfernen. Auf Wunsch lassen wir Ihnen das Holz – sauber gestapelt.',
    whenNeeded: [
      'Großmengen nach Fällung oder Pflege',
      'Brennholz-Erzeugung aus eigenem Bestand',
      'Mulch für Beete oder Wege',
    ],
    process: [
      { title: 'Optionen klären', description: 'Was bleibt, was geht?', icon: 'List' },
      { title: 'Aufarbeiten', description: 'Häckseln, Sägen, Stapeln.', icon: 'Wrench' },
      { title: 'Abfahren', description: 'Wir entsorgen fachgerecht.', icon: 'Truck' },
    ],
    faq: [
      {
        question: 'Bekomme ich Brennholz von Ihnen?',
        answer:
          'Wir lassen das Holz Ihres Baumes Ihnen, wenn Sie wollen – meterweise gesägt und gestapelt. Spalten und Trocknen liegt dann bei Ihnen.',
      },
    ],
    related: ['baumfaellung', 'stubbenfraesen', 'wurzelentfernung'],
    metaDescription:
      'Entsorgung von Schnittgut und Stammholz. Häckseln, Brennholz, Container – Sie entscheiden.',
  },
  {
    slug: 'stubbenfraesen',
    category: 'baumfaellung',
    title: 'Stubbenfräsen',
    shortDescription: 'Wurzelstock weg – ohne Bagger, ohne Großbaustelle.',
    heroImage: placeholder('stubbenfraesen'),
    description:
      'Mit der Stubbenfräse arbeiten wir den Wurzelstock auf 20–30 cm unter Grasnarbe ab. Das reicht für Rasenneueinsaat, Bepflanzung und meist auch für Pflasterung. Schneller und schonender als Bagger-Ausbau.',
    whenNeeded: [
      'Nach Fällung im Rasen oder Beet',
      'Vorbereitung für Neupflanzung oder Pflasterung',
      'Beseitigung von Stolperfallen',
    ],
    process: [
      {
        title: 'Zugang prüfen',
        description: 'Maschine 80 cm breit – passt durch jede Gartentür.',
        icon: 'Ruler',
      },
      { title: 'Fräsen', description: '20–30 cm tief, je nach Folgenutzung.', icon: 'Wrench' },
      {
        title: 'Auffüllen',
        description: 'Mit Mutterboden oder Sand-Kies-Gemisch, je nach Wunsch.',
        icon: 'CircleCheck',
      },
    ],
    faq: [
      {
        question: 'Wie tief wird gefräst?',
        answer: 'Standard 20 cm für Rasen, 30 cm für Pflaster oder Beete. Auf Wunsch auch tiefer.',
      },
    ],
    related: ['wurzelentfernung', 'baumfaellung', 'entsorgung'],
    metaDescription:
      'Stubbenfräsen schnell und schonend. Wir entfernen Wurzelstöcke ohne Bagger – Hamburg & Schleswig-Holstein.',
  },
  {
    slug: 'wurzelentfernung',
    category: 'baumfaellung',
    title: 'Wurzelentfernung',
    shortDescription:
      'Wenn der Stubben raus muss: vollständige Entfernung mit Bagger oder Spezialgerät.',
    heroImage: placeholder('wurzeln'),
    description:
      'Bei Bauvorhaben oder Setzungsproblemen reicht das Stubbenfräsen nicht – die Hauptwurzeln müssen raus. Wir koordinieren Bagger, Schutz von Nachbarwurzeln und Entsorgung der Wurzelmasse.',
    whenNeeded: [
      'Bauvorhaben am ehemaligen Baumstandort',
      'Befestigte Flächen ohne Senkungen',
      'Garten-Neugestaltung',
    ],
    process: [
      {
        title: 'Standort begutachten',
        description: 'Welche Wurzeln müssen raus?',
        icon: 'ScanEye',
      },
      { title: 'Ausbaggern', description: 'Mit Minibagger oder Spezialaufsatz.', icon: 'Wrench' },
      {
        title: 'Entsorgung',
        description: 'Wurzeln werden zerkleinert und abgefahren.',
        icon: 'Truck',
      },
    ],
    faq: [
      {
        question: 'Wie groß ist der Aufwand?',
        answer:
          'Bei einem Großbaum 1–2 Tage und je nach Zugang ein Bagger 1,5–5 t. Wir kalkulieren das vorab.',
      },
    ],
    related: ['stubbenfraesen', 'baumfaellung', 'entsorgung'],
    metaDescription:
      'Wurzelentfernung mit Bagger oder Spezialgerät. Sauber, schnell, mit fachgerechter Entsorgung.',
  },
  {
    slug: 'notfaellung',
    category: 'baumfaellung',
    title: 'Notfällung',
    shortDescription: '24/7 erreichbar. Wir kommen, wenn der Baum gerade fällt oder gefallen ist.',
    heroImage: placeholder('notfall'),
    description:
      'Eine Notfällung ist kein Routinejob: Es geht um Personenschutz und schnelle Sicherung. Wir kommen am selben Tag, sichern die Lage und arbeiten den Baum kontrolliert ab. Versicherungsdokumentation inklusive.',
    whenNeeded: [
      'Akute Standsicherheits-Gefahr',
      'Hängengebliebener Baum oder Astteil',
      'Bruch über Verkehrsflächen',
    ],
    process: [
      { title: 'Anruf', description: 'Wir nehmen sofort Kontakt auf.', icon: 'Phone' },
      {
        title: 'Sofort vor Ort',
        description: 'Im Einsatzgebiet meist binnen Stunden.',
        icon: 'Car',
      },
      {
        title: 'Sichern',
        description: 'Akute Gefahren entschärfen, Zugang absperren.',
        icon: 'ShieldCheck',
      },
      { title: 'Abarbeiten', description: 'Stückweise, kontrolliert, dokumentiert.', icon: 'Axe' },
    ],
    faq: [
      {
        question: 'Wie schnell sind Sie da?',
        answer:
          'Im Einsatzgebiet meist innerhalb von 2–4 Stunden, in Hamburg und Stormarn oft schneller. Bei Sturmlagen halten wir Sie über die Anfahrtszeit auf dem Laufenden.',
      },
    ],
    related: ['sturmschaeden', 'gefahrenfaellung', 'baumfaellung'],
    metaDescription:
      'Notfällung 24/7. Schnell, kontrolliert, mit Versicherungsdokumentation. Hamburg & Schleswig-Holstein.',
  },
  {
    slug: 'genehmigungen',
    category: 'baumfaellung',
    title: 'Genehmigungen',
    shortDescription: 'Baumschutzsatzung, Antrag, Foto-Doku – wir übernehmen den Papierkram.',
    heroImage: placeholder('genehmigung'),
    description:
      'In vielen Gemeinden ist die Fällung ab einer bestimmten Stammstärke genehmigungspflichtig. Wir prüfen die Satzung, stellen den Antrag mit Begründung und Foto-Dokumentation und begleiten die Vor-Ort-Termine mit der Behörde.',
    whenNeeded: [
      'Bäume ab Stammumfang nach Satzung',
      'Naturdenkmäler oder geschützte Arten',
      'Bauvorhaben mit Baumbestand',
    ],
    process: [
      {
        title: 'Satzung prüfen',
        description: 'Welche Schutzregeln gelten in Ihrer Gemeinde?',
        icon: 'FileText',
      },
      {
        title: 'Antrag',
        description: 'Begründung, Fotos, ggf. Ersatzpflanzungs-Vorschlag.',
        icon: 'Pencil',
      },
      {
        title: 'Termin begleiten',
        description: 'Vor-Ort-Begehung mit der Behörde.',
        icon: 'MessagesSquare',
      },
    ],
    faq: [
      {
        question: 'Wie lange dauert eine Genehmigung?',
        answer:
          'In Schleswig-Holstein meist 2–6 Wochen. Bei Bauvorhaben kann es länger dauern, deshalb empfehlen wir frühe Antragstellung.',
      },
    ],
    related: ['baumfaellung', 'baumkontrolle', 'gefahrenfaellung'],
    metaDescription:
      'Fällgenehmigung beantragen. Wir übernehmen Antrag, Begründung und Behördentermine.',
  },
];

export const getServiceBySlug = (slug: string): ServiceContent | undefined =>
  services.find((service) => service.slug === slug);

export const getServicesByCategory = (category: ServiceCategory): ServiceContent[] =>
  services.filter((service) => service.category === category);
